import { Trans, useLingui } from '@lingui/react/macro';
import { ChevronsUpDown, X } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options?: Option[];
  placeholder?: string;
  emptyMessage?: string;
  maxDisplay?: number;
  value?: string[];
  onValueChange?: (value: string[]) => void;
  /** Classes applied to the trigger button. */
  triggerClassName?: string;
}

/**
 * Multi-select combobox — popover trigger that opens a searchable list
 * of options with checkboxes. Selected values render as `<Badge />`
 * chips inside the trigger; clicking the `×` on a chip removes that
 * value without opening the popover.
 *
 * Adapted from `@eds/multiselect`. Local edits from the EDS source:
 *
 * - Function declaration (not `React.FC` arrow const) per
 *   `CODE_CONVENTIONS.md`.
 * - Controlled / uncontrolled selection collapsed into a single
 *   `applyChange` helper instead of a non-null-asserted union
 *   (`onValueChange!`) — the assertion was unsafe because callers can
 *   pass `value` without `onValueChange` (the component would crash on
 *   first selection); the helper now falls back to the internal state
 *   in either branch.
 * - JSDoc on the exported component and the three internal handlers.
 */
export function MultiSelect({
  options = [],
  placeholder,
  emptyMessage,
  maxDisplay = 3,
  value,
  onValueChange,
  triggerClassName,
}: MultiSelectProps) {
  const { t } = useLingui();
  const resolvedPlaceholder = placeholder ?? t`Select items...`;
  const resolvedEmptyMessage = emptyMessage ?? t`No items found.`;
  const [open, setOpen] = useState<boolean>(false);
  const [internalValue, setInternalValue] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');

  const isControlled = value !== undefined;
  const selectedValues = isControlled ? value : internalValue;

  /**
   * Single write-path for selection state. In controlled mode forwards
   * to the consumer's `onValueChange` (no-op if the caller passed
   * `value` without a setter — a permissive shape consistent with
   * other Radix-derived components). In uncontrolled mode updates the
   * internal state.
   */
  function applyChange(next: string[]) {
    if (isControlled) {
      onValueChange?.(next);
    } else {
      setInternalValue(next);
    }
  }

  /** Toggles `optionValue` in the selected set. */
  function handleSelect(optionValue: string) {
    const next = selectedValues.includes(optionValue)
      ? selectedValues.filter((item) => item !== optionValue)
      : [...selectedValues, optionValue];
    applyChange(next);
  }

  /** Removes `optionValue` from the selected set (used by chip `×` buttons). */
  function handleRemove(optionValue: string) {
    applyChange(selectedValues.filter((item) => item !== optionValue));
  }

  /** Clears the entire selection. Surfaced as the first command-list item once anything is selected. */
  function clearAll() {
    applyChange([]);
  }

  const selectedItems = options.filter((option) => selectedValues.includes(option.value));
  const displayItems = selectedItems.slice(0, maxDisplay);
  const remainingCount = selectedItems.length - maxDisplay;

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'border-input w-full flex items-center justify-between min-h-10 h-auto py-2 px-3 text-sm',
              'border rounded-md bg-background cursor-pointer',
              'hover:bg-accent hover:text-accent-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              triggerClassName,
            )}
            onClick={() => setOpen(!open)}
          >
            <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0 pr-2">
              {selectedValues.length === 0 ? (
                <span className="text-muted-foreground">{resolvedPlaceholder}</span>
              ) : (
                <>
                  {displayItems.map((item) => (
                    // Rendered as a span with role="button" rather than a real
                    // <button>: this sits inside the PopoverTrigger's <button>,
                    // and a nested <button> is invalid HTML (React hydration
                    // error). The span keeps click + keyboard removal.
                    <span
                      key={item.value}
                      role="button"
                      tabIndex={0}
                      aria-label={t`Remove ${item.label}`}
                      className="inline-flex"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(item.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemove(item.value);
                        }
                      }}
                    >
                      <Badge variant="secondary" className="flex items-center gap-1 pr-1">
                        {item.label}
                        <X className="h-3 w-3 cursor-pointer hover:text-destructive" />
                      </Badge>
                    </span>
                  ))}
                  {remainingCount > 0 && (
                    <Badge variant="outline">
                      <Trans>+{remainingCount} more</Trans>
                    </Badge>
                  )}
                </>
              )}
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput
              placeholder={t`Search items...`}
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              <CommandEmpty>{resolvedEmptyMessage}</CommandEmpty>
              <CommandGroup>
                {selectedValues.length > 0 && (
                  <CommandItem onSelect={clearAll} className="justify-center text-center">
                    <Trans>Clear all ({selectedValues.length})</Trans>
                  </CommandItem>
                )}
                {options.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => handleSelect(option.value)}
                    >
                      <Checkbox
                        checked={isSelected}
                        className="mr-2 [&_svg]:!text-primary-foreground"
                      />
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
